// texe-web/src/pages/SavWarTopicPage.tsx
// Mobile SavWarTopicScreen'den adapt edildi - Topic bazlı SavWar detay

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet } from '../lib/api';
import { getRankName, getRankIcon, isErRank } from '../lib/ranks';
import Avatar from '../components/Avatar';

interface Author {
  _id: string;
  username: string;
  avatarUrlSmall: string | null;
  savwarTP: number;
  savwarRank: number;
}

interface Post {
  _id: string;
  sessionId: {
    _id: string;
    startAt: string;
    endAt: string;
    active: boolean;
  };
  authorId: Author;
  content: string;
  starsCount: number;
  commentsCount: number;
  createdAt: string;
}

interface Topic {
  _id: string;
  tag: string;
  slug: string;
}

export default function SavWarTopicPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState<Topic | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [displayCount, setDisplayCount] = useState(25);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Rütbe tooltip state
  const [hoveredRank, setHoveredRank] = useState<{
    rank: number;
    username: string;
    tp: number;
  } | null>(null);

  useEffect(() => {
    if (slug) {
      loadTopicPosts();
    }
  }, [slug]);

  const loadTopicPosts = async () => {
    try {
      setLoading(true);
      const response: any = await apiGet(`/api/savwar/topics/${slug}/posts?limit=100`);
      
      if (response && response.success && response.data) {
        setTopic(response.data.topic);
        // Backend'den zaten sıralı geliyor (post performansı: starsCount + commentsCount DESC)
        setPosts(response.data.posts || []);
      }
    } catch (error: any) {
      console.error('[SavWarTopic] Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setDisplayCount(prev => Math.min(prev + 25, posts.length));
      setLoadingMore(false);
    }, 300);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes}dk önce`;
    if (hours < 24) return `${hours}sa önce`;
    return `${days}g önce`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#000',
      }}>
        <div style={{ fontSize: 24, marginBottom: 12, color: '#FFD700' }}>⏳</div>
        <div style={{ fontSize: 16, color: '#666' }}>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#000', minHeight: '100vh' }}>
      {/* Header - Golden Background like mobile */}
      <div style={{
        backgroundColor: '#FFD700',
        padding: 20,
        textAlign: 'center',
        position: 'relative',
      }}>
        <button
          onClick={() => navigate('/savwar')}
          style={{
            position: 'absolute',
            left: 16,
            top: 16,
            background: 'none',
            border: 'none',
            fontSize: 16,
            color: '#000',
            fontWeight: '600',
            cursor: 'pointer',
            zIndex: 10,
          }}
        >
          ← Geri
        </button>
        <h1 style={{
          fontSize: 32,
          fontWeight: 'bold',
          color: '#000',
          marginBottom: 4,
          margin: 0,
        }}>
          {topic?.tag || slug}
        </h1>
        <p style={{
          fontSize: 14,
          color: '#333',
          margin: 0,
        }}>
          Tüm Zamanlar - Post Performansı
        </p>
      </div>

      {/* Posts Container */}
      <div style={{ padding: 16 }}>
        <h2 style={{
          fontSize: 18,
          fontWeight: 'bold',
          color: '#FFD700',
          marginBottom: 16,
        }}>
          📊 {posts.length} Post (Yıldız + Yorum)
        </h2>

        {posts.length === 0 ? (
          <div style={{
            fontSize: 16,
            color: '#666',
            textAlign: 'center',
            marginTop: 40,
          }}>
            Henüz post yok
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {posts.slice(0, displayCount).map((post, index) => (
                <div
                  key={post._id}
                  style={{
                    backgroundColor: '#1a1a1a',
                    border: '2px solid #114232',
                    borderRadius: 16,
                    padding: 16,
                    marginBottom: 12,
                  }}
                >
                  {/* Post Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 12,
                  }}>
                    {/* Left Section: Rank + Rank Icon + TP */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1,
                    }}>
                      <div style={{
                        fontSize: 18,
                        fontWeight: 'bold',
                        color: '#FFD700',
                        marginRight: 12,
                      }}>
                        #{index + 1}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}>
                        {/* Rank Icon - Hover ile tooltip */}
                        <div
                          style={{
                            position: 'relative',
                            cursor: 'help',
                          }}
                          onMouseEnter={() => setHoveredRank({
                            rank: post.authorId.savwarRank,
                            username: post.authorId.username,
                            tp: post.authorId.savwarTP,
                          })}
                          onMouseLeave={() => setHoveredRank(null)}
                        >
                          {isErRank(post.authorId.savwarRank) ? (
                            <span style={{ fontSize: 20 }}>🟢</span>
                          ) : (
                            <img 
                              src={getRankIcon(post.authorId.savwarRank)}
                              alt={getRankName(post.authorId.savwarRank)}
                              style={{
                                width: 24,
                                height: 24,
                                objectFit: 'contain',
                              }}
                            />
                          )}
                        </div>
                        {/* TP */}
                        <div style={{
                          fontSize: 14,
                          color: '#FFD700',
                          fontWeight: 'bold',
                        }}>
                          🗡️ {post.authorId.savwarTP?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>

                    {/* Right Section: Avatar + Username */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/u/${post.authorId.username}`);
                      }}
                    >
                      <Avatar
                        src={post.authorId.avatarUrlSmall}
                        alt={post.authorId.username}
                        size={50}
                        variant="ring"
                        ringColor="#FFD700"
                      />
                      <div style={{
                        fontSize: 12,
                        color: '#FFD700',
                        fontWeight: '600',
                        marginTop: 4,
                      }}>
                        {post.authorId.username}
                      </div>
                    </div>
                  </div>

                  {/* Post Content */}
                  <div style={{
                    fontSize: 16,
                    color: '#fff',
                    marginBottom: 24,
                    lineHeight: 1.5,
                    textAlign: 'left',
                  }}>
                    {post.content}
                  </div>

                  {/* Post Footer */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <div style={{
                      display: 'flex',
                      gap: 16,
                    }}>
                      <span style={{ fontSize: 14, color: '#666' }}>
                        ⭐ {post.starsCount}
                      </span>
                      <span 
                        style={{ fontSize: 14, color: '#666', cursor: 'pointer' }}
                        onClick={() => navigate(`/savwar/post/${post._id}`)}
                      >
                        💬 {post.commentsCount}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {formatDate(post.createdAt)} • {post.sessionId.active ? ' 🟢 Aktif' : ' 🔴 Arşiv'}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {displayCount < posts.length && (
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                style={{
                  width: '100%',
                  backgroundColor: '#FFD700',
                  padding: '14px 24px',
                  borderRadius: 8,
                  marginTop: 16,
                  marginBottom: 10,
                  border: 'none',
                  fontSize: 16,
                  fontWeight: 'bold',
                  color: '#000',
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                  opacity: loadingMore ? 0.5 : 1,
                }}
              >
                {loadingMore ? 'Yükleniyor...' : 'Daha Fazla Göster'}
              </button>
            )}

            {/* End Message */}
            {displayCount >= posts.length && posts.length > 25 && (
              <div style={{
                backgroundColor: '#1a1a1a',
                borderRadius: 8,
                marginTop: 20,
                marginBottom: 10,
                padding: '12px 0',
                textAlign: 'center',
              }}>
                <div style={{
                  fontSize: 14,
                  color: '#888',
                  fontWeight: '600',
                }}>
                  Tüm {posts.length} post gösteriliyor
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Rütbe Tooltip Overlay */}
      {hoveredRank && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#1a1a1a',
          borderRadius: 20,
          padding: 30,
          border: '3px solid #FFD700',
          boxShadow: '0 0 30px rgba(255, 215, 0, 0.5)',
          zIndex: 9999,
          minWidth: 250,
          textAlign: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            fontSize: 24,
            fontWeight: 'bold',
            color: '#FFD700',
            marginBottom: 15,
          }}>
            {getRankName(hoveredRank.rank)}
          </div>
          
          <div style={{
            marginBottom: 15,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            {isErRank(hoveredRank.rank) ? (
              <span style={{ fontSize: 100 }}>🟢</span>
            ) : (
              <img
                src={getRankIcon(hoveredRank.rank)}
                alt={getRankName(hoveredRank.rank)}
                style={{
                  width: 100,
                  height: 100,
                  objectFit: 'contain',
                }}
              />
            )}
          </div>
          
          <div style={{
            fontSize: 20,
            fontWeight: '600',
            color: '#fff',
            marginBottom: 10,
          }}>
            {hoveredRank.username}
          </div>
          
          <div style={{
            fontSize: 18,
            fontWeight: 'bold',
            color: '#4CAF50',
          }}>
            🗡️ {hoveredRank.tp.toFixed(2)} TP
          </div>
        </div>
      )}
    </div>
  );
}